import type { API } from '@/api';

type PersonalDeviceReservation =
  API['POST']['CreateReservation']['response']['reservation'];

type SharedDeviceReservation =
  API['POST']['CreateSharedDeviceReservation']['response']['reservation'];

// フロアマップパス生成に必要なロケーション情報
type FloorMapLocationInfo = Pick<
  PersonalDeviceReservation,
  'airport' | 'flight_type' | 'terminal' | 'floor' | 'lounge'
>;

/**
 * 成田5サテライトの共通ラウンジかどうかを判定
 *
 * @param location - ロケーション情報
 * @returns 成田5サテライトの共通ラウンジの場合true
 */
const isNRT5CommonLounge = (
  location: Pick<FloorMapLocationInfo, 'airport' | 'terminal' | 'lounge'>,
): boolean => {
  return (
    location.lounge === 'common' &&
    location.airport === 'narita' &&
    location.terminal === 'satellite5'
  );
};

/**
 * 成田5サテライト共通ラウンジにおける実際のラウンジタイプを判定
 *
 * @param lounge - ラウンジタイプ
 * @returns 'suite_lounge' または 'ana_lounge'
 */
const getNRT5ActualLoungeType = (
  lounge: string,
): 'suite_lounge' | 'ana_lounge' => {
  // suite_loungeの場合はそのまま返す
  if (lounge === 'suite_lounge') {
    return 'suite_lounge';
  }
  // commonの場合はana_loungeを返す
  if (lounge === 'common') {
    return 'ana_lounge';
  }
  // ana_loungeまたはその他の場合
  return 'ana_lounge';
};

/**
 * フロアマップ画像パスを生成（個人端末用）
 * パターン: {空港}_{便種別}_{ターミナル}_{フロア}f_{ラウンジタイプ}.webp
 * 例: haneda_international_terminal2_4f_ana_lounge.webp
 * NOTE: 成田5サテのみcommonの時、loungeタイプによってSLかALを判定する
 *
 * 個人端末は確認画面と予約完了画面でマップ画像を表示する
 *
 * @param location - ロケーション情報（予約レスポンスまたは部分データ）
 * @returns フロアマップ画像のパス
 */
export const getPersonalDeviceFloorMapImagePath = (
  location: FloorMapLocationInfo,
): string => {
  let loungeType = location.lounge;
  if (isNRT5CommonLounge(location)) {
    loungeType = getNRT5ActualLoungeType(location.lounge);
  }

  return `/floorMap/personalDevice/${location.airport}_${location.flight_type}_${location.terminal}_${location.floor}f_${loungeType}.webp`;
};

/**
 * フロアマップ画像パスを生成（共有端末用）
 * パターン: {空港}_{便種別}_{ターミナル}_{フロア}f_{ラウンジタイプ}_{端末設置フロア}.webp
 * 例: haneda_international_terminal2_3f_ana_lounge_4.webp (3階のANAラウンジマップを4階設置端末用に表示)
 * NOTE: 成田5サテのみcommonの時、loungeタイプによってSLかALを判定する
 *
 * @param reservation - 共有端末予約レスポンス
 * @returns フロアマップ画像のパス
 */
export const getSharedDeviceFloorMapImagePath = (
  reservation: SharedDeviceReservation,
): string => {
  let loungeType = reservation.lounge;
  if (isNRT5CommonLounge(reservation)) {
    loungeType = getNRT5ActualLoungeType(reservation.lounge);
  }

  return `/floorMap/sharedDevice/${reservation.airport}_${reservation.flight_type}_${reservation.terminal}_${reservation.floor}f_${loungeType}_${reservation.device_floor}.webp`;
};
